"use client";
import { useState, useEffect } from "react";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { useRouter, useSearchParams } from "next/navigation";
import { ColumnDef } from "@tanstack/react-table";
import { fetchData } from "@/utils/api-handler";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { PermissionGate } from "@/components/shared/permission-gate";
import { AccessDeniedCard } from "@/components/shared/access-denied-card";
import { format } from "date-fns";

import {
  ArrowUpDown,
  ClipboardPlus,
  MoreHorizontal,
  Search,
  X,
  File,
  ExternalLink,
  Eye,
  Trash2,
  Tag,
  Copy,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

import { Input } from "@/components/ui/input";
import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/shared/data-table";
import { Loader } from "@/components/ui/loader";
import { getPageRange, DEFAULT_TABLE_SORT_BY_CREATED_AT, SEARCH_DEBOUNCE_WAIT } from "@/utils/global";
import { toast } from "sonner";
import { DeleteConfirmationDialog } from "@/components/shared/delete-dialog";
import { getDocumentsColumns } from "@/components/documents/tables/documents-columns";
import TablePagination from "@/components/shared/table-pagination";
import { cn } from "@/lib/utils";

interface DocumentListProps {
  activeSection?: string;
  constrainTableHeight?: boolean;
  setActiveSection?: React.Dispatch<React.SetStateAction<string>>;
  showAddDocumentsButton?: boolean;
}

interface DataSourcesAllResponse {
  datasources: DataSource[];
}

interface ActionCellProps {
  row: { original: DocumentFile };
  setActiveSection?: React.Dispatch<React.SetStateAction<string>>;
}

export const DocumentList: React.FC<DocumentListProps> = ({
  constrainTableHeight = true,
  setActiveSection,
  showAddDocumentsButton = true,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: SEARCH_DEBOUNCE_WAIT });
  const [selectedDataSource, setSelectedDataSource] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedFileType, setSelectedFileType] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(() => parseInt(searchParams.get("page") || "1", 10) || 1);
  const [pageSize] = useState(10);
  const [openDialog, setOpenDialog] = useState<{ [key: string]: boolean }>({});
  const [sortBy, setSortBy] = useState<string>(DEFAULT_TABLE_SORT_BY_CREATED_AT.sortBy);
  const [sortOrder, setSortOrder] = useState<string>(DEFAULT_TABLE_SORT_BY_CREATED_AT.sortOrder);

  const handleSort = (column: string) => {
    setSortBy(column);
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const { data: datasourcesData, hasPermission: hasListDataSourcesPermission } =
    useProtectedQuery<DataSourcesAllResponse>({
      queryKey: ["datasources-all-filter"],
      queryFn: async () => fetchData("/api/documents/datasources/all"),
      requiredPermissions: ["LIST_DATA_SOURCES"],
    });

  const { data: documentsData, isLoading, hasPermission } = useProtectedQuery<DocumentFilesResponse>({
    queryKey: [
      "documents-files",
      debouncedSearch,
      selectedDataSource,
      selectedStatus,
      selectedFileType,
      currentPage,
      sortBy,
      sortOrder,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      if (selectedDataSource !== "all") {
        params.append("datasource_id", selectedDataSource);
      }
      if (selectedStatus !== "all") {
        params.append("status", selectedStatus);
      }
      if (selectedFileType !== "all") {
        params.append("type", selectedFileType);
      }

      return fetchData(`/api/documents/files?${params.toString()}`);
    },
    requiredPermissions: ["LIST_DOCUMENTS"],
  });

  useEffect(() => {
    if (documentsData?.page_count && currentPage > documentsData.page_count) {
      setCurrentPage(1);
      const params = new URLSearchParams(searchParams.toString());
      params.set("page", "1");
      router.replace(`${window.location.pathname}?${params.toString()}`);
    }
  }, [documentsData?.page_count, currentPage, router, searchParams]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDataSource]);

  const formatFileSize = (sizeString: string): string => {
    const bytes = parseInt(sizeString);
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "UPLOADING":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "UPLOADED":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "PROCESSED":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
      case "FAILED":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const truncateFileName = (name: string, maxLength: number = 15): string => {
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength) + "...";
  };

  const handleDialogOpen = (documentId: string, isOpen: boolean) => {
    setOpenDialog(prev => ({
      ...prev,
      [documentId]: isOpen
    }));
  };


  const columns = getDocumentsColumns({
    setActiveSection,
    formatFileSize,
    getStatusColor,
    truncateFileName,
    openDialog,
    handleDialogOpen,
    sortBy,
    sortOrder,
    handleSort,
  });

  return (
    <>
      {!hasPermission && (
        <AccessDeniedCard message="You don't have permission to list documents. Contact your administrator if you need access." />
      )}
      {hasPermission && (
        <>
          <div className="mb-5 flex flex-col gap-3">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="relative w-full shrink-0 xl:max-w-[280px]">
                <Input
                  className="w-full pl-8"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  startIcon={
                    <Search className="h-4 w-4 tex absolute text-muted-foreground top-1/2 transform -translate-y-1/2" />
                  }
                />
              </div>
              <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:flex xl:w-auto xl:flex-row xl:items-center xl:gap-3">
                {hasListDataSourcesPermission && (
                  <div className="w-full min-w-0 sm:col-span-2 md:col-span-1 xl:w-[200px]">
                    <Select
                      value={selectedDataSource}
                      onValueChange={setSelectedDataSource}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="All Data Sources" />
                      </SelectTrigger>
                      <SelectContent className="shadow-lg rounded-lg">
                        <SelectItem value="all">All Data Sources</SelectItem>
                        {datasourcesData?.datasources?.map((datasource) => (
                          <SelectItem key={datasource.id} value={datasource.id}>
                            {datasource.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="w-full min-w-0 xl:w-40">
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent className="shadow-lg rounded-lg">
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="UPLOADING">Uploading</SelectItem>
                      <SelectItem value="UPLOADED">Uploaded</SelectItem>
                      <SelectItem value="PROCESSED">Processed</SelectItem>
                      <SelectItem value="FAILED">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-full min-w-0 xl:w-40">
                  <Select
                    value={selectedFileType}
                    onValueChange={setSelectedFileType}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent className="shadow-lg rounded-lg">
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="PDF">PDF</SelectItem>
                      <SelectItem value="TXT">TXT</SelectItem>
                      <SelectItem value="DOCX">DOCX</SelectItem>
                      <SelectItem value="CSV">CSV</SelectItem>
                      <SelectItem value="MP3">MP3</SelectItem>
                      <SelectItem value="WAV">WAV</SelectItem>
                      <SelectItem value="MP4">MP4</SelectItem>
                      <SelectItem value="AVI">AVI</SelectItem>
                      <SelectItem value="MOV">MOV</SelectItem>
                      <SelectItem value="UNKNOWN">UNKNOWN</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {showAddDocumentsButton && (
                  <PermissionGate requiredPermissions={["CREATE_DOCUMENT"]}>
                    <div className="w-full sm:col-span-2 md:col-span-3 xl:col-span-1 xl:w-auto">
                      <Button
                        className="w-full xl:w-auto"
                        onClick={() => {
                          setActiveSection?.("upload");
                        }}
                      >
                        <ClipboardPlus className="h-4 w-4 mr-1" /> Add Documents
                      </Button>
                    </div>
                  </PermissionGate>
                )}
              </div>
            </div>
          </div>
          <div className="relative overflow-hidden border rounded-lg">
            {isLoading && <Loader />}
            <div
              className={cn(
                "overflow-x-auto",
                constrainTableHeight && "max-h-[calc(80vh-150px)] overflow-y-auto"
              )}
            >
            <div className="overflow-auto max-h-[calc(80vh-189px)]">
            <DataTable
                  columns={columns}
                  data={documentsData?.documents ?? []}
                />
              </div>
            </div>
          </div>
          <TablePagination
            currentPage={currentPage}
            pageCount={documentsData?.page_count || 0}
            onPageChange={setCurrentPage}
            pageParamName="page"
          />

          {/* Render all dialogs in the main return */}
          {documentsData?.documents?.map((document) => {
            const tags = document.tags || [];
            if (tags.length <= 2) return null;

            return (
              <Dialog
                key={document.id}
                open={openDialog[document.id] || false}
                onOpenChange={(isOpen) => handleDialogOpen(document.id, isOpen)}
              >
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Tag className="h-5 w-5" />
                      Document Labels
                    </DialogTitle>
                    <DialogDescription>
                      Document labels generated by the AI.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-6">
                    {/* All Tags */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-foreground">All Labels</h4>
                      {tags.length > 0 ? (
                        <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto p-2 border rounded-md bg-muted/30">
                          {tags.map((tag, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-2 p-2 bg-background rounded border"
                            >
                              <Tag className="h-3 w-3 text-muted-foreground" />
                              <span className="text-sm font-medium">{tag}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground p-4 text-center border rounded-md bg-muted/30">
                          No labels assigned
                        </p>
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            );
          })}
        </>
      )}
    </>
  );
};

